import {
  BriefcaseBusiness,
  Calculator,
  Contact,
  FolderOpen,
  Handshake,
  HelpCircle,
  House,
  Settings,
  UserCog,
  Users
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import { usePartnerAdminAccess } from '@/pages/partner/usePartnerAdminAccess';
import { NavMain, type NavMainItem } from '@/layout/nav/NavMain';

// NavMain builds links as `/<firstPathSegment><item.url>`, so inside the
// `/partner` area `url: '/leads'` resolves to the leads list.

const homeNavItem: NavMainItem = {
  title: 'partner.home.title',
  url: '/',
  icon: House
};

// Everything a selling partner works with sits in one group, in the order of
// the tabs on its pages (PartnerSalesTabsNav). Partners who only run service
// projects for BLIKS customers will come with the license model; that group
// is then shown by capability instead of to every partner.
const salesNavItem: NavMainItem = {
  title: 'partner.nav.sales',
  icon: BriefcaseBusiness,
  items: [
    { title: 'partner.leads.title', url: '/leads', icon: Users },
    { title: 'partner.contacts.title', url: '/contacts', icon: Contact },
    { title: 'partner.calculator.title', url: '/calculator', icon: Calculator },
    { title: 'partner.infoHub.title', url: '/info-hub', icon: FolderOpen },
    { title: 'partner.program.title', url: '/program', icon: Handshake }
  ]
};

// Settings is the only partner_admin-only nav entry. It's a group, so NavMain
// renders the side pop-out of its tabs — profile and user management —
// mirroring the customer app's settings area (where User Management also lives
// under Settings).
const adminNavItems: NavMainItem[] = [
  {
    title: 'common.Sidebar.Settings',
    url: '/organization',
    icon: Settings,
    items: [
      {
        title: 'common.Sidebar.Settings',
        url: '/organization',
        icon: Settings
      },
      {
        title: 'partner.settings.tabs.users',
        url: '/organization/users',
        icon: UserCog
      }
    ]
  }
];

export function PartnerNavSidebar() {
  const { t } = useTranslation();
  const { isPartnerAdmin } = usePartnerAdminAccess();
  const navigation: NavMainItem[] = [
    homeNavItem,
    salesNavItem,
    ...(isPartnerAdmin ? adminNavItems : [])
  ];

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="flex flex-col w-14 bg-sidebar text-sidebar-foreground shrink-0">
        {/* Main navigation */}
        <div className="flex-1 overflow-y-auto">
          <NavMain items={navigation} />
        </div>

        {/* Footer */}
        <div className="p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href="https://help.bliks.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full h-10 transition-colors rounded-md hover:bg-sidebar-accent"
              >
                <HelpCircle className="h-[21px] w-[21px] text-fg-secondary" strokeWidth={1.5} />
              </a>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              {t('common.Help')}
            </TooltipContent>
          </Tooltip>
        </div>
      </nav>
    </TooltipProvider>
  );
}
